import { useCallback, useReducer } from 'react'

type ModalState<T = any> = {
  isActive: boolean
  data?: T
}

type ModalStateAction<T = any> = {
  type: 'active' | 'inactive' | 'toggle'
  payload?: T | { clearOnClose?: boolean }
}

const reducer =
  <T = any>() =>
  (state: ModalState<T>, action: ModalStateAction<T>) => {
    switch (action.type) {
      case 'active':
        return {
          isActive: true,
          data: action.payload || null,
        }
      case 'inactive':
        return {
          isActive: false,
          data: action.payload?.['clearOnClose'] ? null : state.data,
        }
      case 'toggle':
        return {
          ...state,
          isActive: !state.isActive,
        }
    }
  }

export const useModalState = <T = any>(clearOnClose = false) => {
  const [state, dispatch] = useReducer(reducer<T>(), {
    isActive: false,
    data: null,
  })

  const open = useCallback((data?: T) => {
    dispatch({ type: 'active', payload: data })
  }, [])

  const close = useCallback(() => {
    dispatch({
      type: 'inactive',
      payload: { clearOnClose },
    })
  }, [clearOnClose])

  const toggle = useCallback(() => {
    dispatch({ type: 'toggle' })
  }, [])

  return {
    isActive: state.isActive,
    data: state.data,
    open,
    close,
    toggle,
  }
}
